/**
 * Custom hook untuk mengambil semua data inventory category
 * 
 * API Endpoint: GET `/api/inventory-category`
 * API Version: 3.0
 * 
 * @param {string} id - ID inventory category
 * @param {string} name - Nama inventory category
 * @returns {object} - Semua data inventory category yang telah diformat
 * @example
 * const { data, isLoading, error } = useIndexInventoryCategory();
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { InventoryCategoryResponseSchema } from "../response/IndexInventoryCategoryResponse";

const API_VERSION = "v4";

interface FilterProps {
    all?: number;
    id?: string;
    name?: string;
}

interface IndexInventoryCategoryProps {
    filters?: FilterProps,
    search?: string,
    limit?: number,
    queryKey?: string,
    page?: number,
}

const useIndexInventoryCategory = (query: IndexInventoryCategoryProps) => {
    return useQuery({
        queryKey: [
            'inventory-categories',
            query.filters?.all ? 'all' : '',
            ...Object.entries(query.filters ?? {})
                .filter(([key, value]) => key !== 'all' && value !== undefined && value !== null && value !== "")
                .map(([key, value]) => `${key}:${value}`),
            query.search,
            query.queryKey,
            query.page,
        ],
        queryFn: async () => {
            try {
                const params = query.filters?.all
                    ? { all: 1 }
                    : {
                        filter: Object.fromEntries(
                            Object.entries(query.filters ?? {}).filter(([key, value]) => key !== "all" && value !== undefined && value !== null && value !== "")
                        ),
                        search: query.search || "",
                        limit: query.limit ?? 10,
                        page: query.page,
                    };

                const response = await privateApi.get(`/${API_VERSION}/inventory-category`, { params });

                // Validasi response
                const validationResult = InventoryCategoryResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Inventory Category data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Inventory Category`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useIndexInventoryCategory;
